import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useNotifications } from "@/hooks/useNotifications"
import { cn } from "@/lib/utils"
import type { NotificationItem } from "@/types"
import { Link } from "@tanstack/react-router"
import { Check, Loader2, Mail, MailOpen, Trash2, X } from "lucide-react"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { memo, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"

const NotificationRow = memo(function NotificationRow({
  n,
  onMarkRead,
  onDelete,
  onClose,
}: {
  n: NotificationItem
  onMarkRead: (id: string) => void
  onDelete: (id: string) => void
  onClose: () => void
}) {
  const { t } = useTranslation()
  return (
    <div className="w-full">
      <div className="box-border flex w-full items-start gap-3 px-3 py-2">
        <div className="mt-0.5">
          <span
            className={cn(
              "inline-block h-2.5 w-2.5 rounded-full",
              n.is_read ? "bg-muted" : "bg-primary",
            )}
          />
        </div>
        <div className="min-w-0 flex-1">
          <Tooltip delayDuration={700}>
            <TooltipTrigger asChild>
              <Link
                to={n.redirect_url || "/"}
                target="_blank"
                className="block max-w-full overflow-hidden"
                onClick={() => {
                  if (!n.is_read) onMarkRead(n.id)
                  onClose()
                }}
              >
                <div className="truncate font-medium">{n.title}</div>
                <div className="text-muted-foreground truncate text-sm">
                  {n.message}
                </div>
              </Link>
            </TooltipTrigger>
            <TooltipContent>{t("notify_user.openInNewTab")}</TooltipContent>
          </Tooltip>

          <div className="text-muted-foreground mt-1 flex items-center gap-2 text-xs">
            <span>{n.sender_username}</span>
            {n.created_at && (
              <span>• {new Date(n.created_at).toLocaleString()}</span>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {!n.is_read && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 cursor-pointer"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onMarkRead(n.id)
              }}
              title={t("markAsRead")}
              aria-label={t("markAsRead")}
            >
              <Check className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 cursor-pointer"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onDelete(n.id)
            }}
            title={t("delete")}
            aria-label={t("delete")}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <DropdownMenuSeparator />
    </div>
  )
})

export default function NotificationsDropdown({
  className,
  contentClassName,
}: {
  className?: string
  contentClassName?: string
}) {
  const [open, setOpen] = useState(false)
  const {
    notifications,
    unreadCount,
    deleteOne,
    markRead,
    markAllRead,
    clearAll,
  } = useNotifications()

  const { t } = useTranslation()
  const count = notifications.length
  const handleMarkRead = useCallback(
    (id: string) => markRead.mutate(id),
    [markRead],
  )
  const handleDelete = useCallback(
    (id: string) => deleteOne.mutate(id),
    [deleteOne],
  )
  const handleClose = useCallback(() => setOpen(false), [])

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn("relative cursor-pointer rounded-full", className)}
          aria-label={t("notifications")}
        >
          {unreadCount ? (
            <Mail className="h-5 w-5" />
          ) : (
            <MailOpen className="h-5 w-5" />
          )}
          {unreadCount > 0 && (
            <span className="bg-primary text-primary-foreground absolute -top-1 -right-1 h-5 min-w-5 rounded-full px-1 text-center text-[10px] leading-5">
              {unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className={cn("box-border w-96 overflow-hidden p-0", contentClassName)}
      >
        <div className="flex items-center justify-between px-3 py-2">
          <DropdownMenuLabel className="p-0">
            {t("notifications")}
          </DropdownMenuLabel>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              disabled={markAllRead.isPending || unreadCount === 0}
              onClick={() => markAllRead.mutate()}
              className="h-7 cursor-pointer px-2"
            >
              {markAllRead.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Check className="mr-1 h-4 w-4" />
                  {t("markAllAsRead")}
                </>
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={clearAll.isPending || count === 0}
              onClick={() => clearAll.mutate()}
              className="h-7 cursor-pointer px-2"
            >
              {clearAll.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Trash2 className="mr-1 h-4 w-4" />
                  {t("clearAll")}
                </>
              )}
            </Button>
          </div>
        </div>
        <DropdownMenuSeparator />

        <div className="max-h-96 overflow-x-hidden overflow-y-auto">
          {count === 0 ? (
            <div className="text-muted-foreground py-6 text-center text-sm">
              {t("noNotifications")}
            </div>
          ) : (
            notifications.map((n) => (
              <NotificationRow
                key={n.id}
                n={n}
                onMarkRead={handleMarkRead}
                onDelete={handleDelete}
                onClose={handleClose}
              />
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
